import React, { useMemo } from "react";
import { fmtMoney, fmtPct } from "@/lib/format";
import { PieChart as PieIcon, TrendingDown, TrendingUp, CheckCircle2, XCircle, Pencil, Coins } from "lucide-react";
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip as RTooltip, BarChart, Bar, XAxis, YAxis, CartesianGrid, Legend } from "recharts";

const CHART_COLORS = ["#3b82f6", "#10b981", "#f59e0b", "#8b5cf6", "#ec4899", "#06b6d4", "#14b8a6", "#f97316"];

function Kpi({ label, value, sub, tone = "default", icon: Icon, testId }) {
  const tones = {
    default: "text-foreground",
    savings: "text-emerald-600",
    danger: "text-red-600",
    warning: "text-amber-600",
    primary: "text-primary",
  };
  return (
    <div className="kpi-card" data-testid={testId}>
      <div className="flex items-center justify-between">
        <div className="text-xs font-mono uppercase tracking-wider text-muted-foreground">{label}</div>
        {Icon && <Icon className={`h-4 w-4 ${tones[tone]}`} />}
      </div>
      <div className={`mt-3 num text-2xl sm:text-3xl font-bold ${tones[tone]}`}>{value}</div>
      {sub && <div className="mt-1 text-xs text-muted-foreground">{sub}</div>}
    </div>
  );
}

export default function OverviewTab({ data }) {
  const t = data.totals;
  const sections = data.sections;
  const rows = data.rows;

  const top10 = useMemo(() => {
    return [...rows]
      .filter(r => r.included && r.current_amount > 0)
      .sort((a, b) => b.current_amount - a.current_amount)
      .slice(0, 10)
      .map(r => {
        const firstLine = r.description.split("\n")[0].replace(/^Supply and (installation|apply) of\s*/i, "");
        return {
          name: `${r.item_no_display} — ${firstLine}`,
          short: firstLine.length > 32 ? firstLine.slice(0, 30) + "…" : firstLine,
          section: r.section_title,
          amount: r.current_amount,
        };
      });
  }, [rows]);

  const pieData = sections.filter(s => s.revised > 0).map(s => ({
    name: s.section_title,
    value: s.revised,
    section_no: s.section_no,
  }));

  const varianceTone = t.variance < 0 ? "savings" : t.variance > 0 ? "danger" : "default";

  return (
    <div className="space-y-6">
      {/* KPI cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Kpi
          testId="kpi-grand-total"
          label="Grand Total (incl. VAT)"
          value={fmtMoney(t.grand_total)}
          sub={`Subtotal ${fmtMoney(t.subtotal_after_markup)} + VAT ${fmtMoney(t.vat_amount)}`}
          tone="primary"
          icon={Coins}
        />
        <Kpi
          testId="kpi-original-total"
          label="Original Baseline"
          value={fmtMoney(t.original_total_with_vat)}
          sub={`Pre-VAT ${fmtMoney(t.original_subtotal)}`}
          icon={PieIcon}
        />
        <Kpi
          testId="kpi-variance"
          label={t.variance <= 0 ? "Savings vs Baseline" : "Over Baseline"}
          value={`${t.variance < 0 ? "-" : "+"}${fmtMoney(Math.abs(t.variance))}`}
          sub={fmtPct(Math.abs(t.variance_pct))}
          tone={varianceTone}
          icon={t.variance <= 0 ? TrendingDown : TrendingUp}
        />
        <Kpi
          testId="kpi-inclusion"
          label="Item Inclusion"
          value={`${t.included_count} / ${t.included_count + t.excluded_count}`}
          sub={`${t.excluded_count} excluded · ${t.edited_count} edited`}
          tone={t.excluded_count > 0 ? "warning" : "savings"}
          icon={t.excluded_count > 0 ? XCircle : CheckCircle2}
        />
      </div>

      {/* Charts row */}
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
        <div className="lg:col-span-2 rounded-xl border border-border bg-card p-5" data-testid="chart-by-section">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-heading text-lg font-bold">Cost by Section</h3>
            <span className="section-pill">{pieData.length} sections</span>
          </div>
          <div className="h-[320px] min-w-0">
            <ResponsiveContainer width="100%" height="100%" minWidth={0}>
              <PieChart>
                <Pie data={pieData} dataKey="value" nameKey="name" innerRadius={65} outerRadius={110} paddingAngle={2}>
                  {pieData.map((_, i) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} stroke="hsl(var(--card))" strokeWidth={2} />)}
                </Pie>
                <RTooltip
                  formatter={(v) => fmtMoney(v)}
                  contentStyle={{ background: "hsl(var(--popover))", border: "1px solid hsl(var(--border))", borderRadius: 8, fontSize: 12 }}
                />
              </PieChart>
            </ResponsiveContainer>
          </div>
          <div className="mt-3 space-y-1.5 max-h-40 overflow-auto pr-1">
            {pieData.map((s, i) => (
              <div key={s.name} className="flex items-center justify-between text-xs">
                <div className="flex items-center gap-2 min-w-0">
                  <span className="h-2.5 w-2.5 rounded-sm shrink-0" style={{ background: CHART_COLORS[i % CHART_COLORS.length] }} />
                  <span className="truncate text-muted-foreground">{s.name}</span>
                </div>
                <span className="num font-semibold shrink-0 ml-2">{fmtMoney(s.value, false)}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="lg:col-span-3 rounded-xl border border-border bg-card p-5" data-testid="chart-top10">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-heading text-lg font-bold">Top 10 Cost Drivers</h3>
            <span className="section-pill">by revised amount</span>
          </div>
          <div className="h-[380px] min-w-0">
            <ResponsiveContainer width="100%" height="100%" minWidth={0}>
              <BarChart data={top10} layout="vertical" margin={{ left: 10, right: 30 }}>
                <CartesianGrid stroke="hsl(var(--border))" strokeDasharray="3 3" horizontal={false} />
                <XAxis type="number" tick={{ fill: "hsl(var(--muted-foreground))", fontSize: 11 }} tickFormatter={(v) => `${Math.round(v/1000)}k`} />
                <YAxis type="category" dataKey="short" width={220} tick={{ fill: "hsl(var(--muted-foreground))", fontSize: 10 }} interval={0} />
                <RTooltip
                  formatter={(v) => fmtMoney(v)}
                  labelFormatter={(l, p) => p[0]?.payload?.name || l}
                  contentStyle={{ background: "hsl(var(--popover))", border: "1px solid hsl(var(--border))", borderRadius: 8, fontSize: 12 }}
                />
                <Bar dataKey="amount" fill="hsl(var(--primary))" radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* Section table */}
      <div className="rounded-xl border border-border bg-card overflow-hidden" data-testid="section-summary-table">
        <div className="px-5 py-4 border-b border-border flex items-center justify-between">
          <h3 className="font-heading text-lg font-bold">Section Breakdown</h3>
          <span className="text-xs text-muted-foreground font-mono uppercase tracking-wider">Original vs Revised</span>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wider text-muted-foreground border-b border-border">
                <th className="px-5 py-3 w-10">#</th>
                <th className="px-5 py-3">Section</th>
                <th className="px-5 py-3 text-right">Items (Inc.)</th>
                <th className="px-5 py-3 text-right">Original</th>
                <th className="px-5 py-3 text-right">Revised</th>
                <th className="px-5 py-3 text-right">Delta</th>
                <th className="px-5 py-3 text-right">%</th>
              </tr>
            </thead>
            <tbody>
              {sections.map((s, i) => {
                const delta = s.revised - s.original;
                const pct = s.original ? (delta / s.original * 100) : 0;
                const deltaClass = delta < 0 ? "text-emerald-600" : delta > 0 ? "text-red-600" : "text-muted-foreground";
                return (
                  <tr key={s.section_no} className="border-b border-border/60 hover:bg-muted/30" data-testid={`section-row-${s.section_no}`}>
                    <td className="px-5 py-3">
                      <span className="inline-flex h-6 w-6 items-center justify-center rounded text-xs font-mono font-bold" style={{ background: CHART_COLORS[i % CHART_COLORS.length] + "20", color: CHART_COLORS[i % CHART_COLORS.length] }}>
                        {s.section_no}
                      </span>
                    </td>
                    <td className="px-5 py-3 font-medium">{s.section_title}</td>
                    <td className="px-5 py-3 text-right num text-muted-foreground">{s.included} / {s.items}</td>
                    <td className="px-5 py-3 text-right num">{fmtMoney(s.original, false)}</td>
                    <td className="px-5 py-3 text-right num font-semibold">{fmtMoney(s.revised, false)}</td>
                    <td className={`px-5 py-3 text-right num ${deltaClass}`}>{delta > 0 ? "+" : ""}{fmtMoney(delta, false)}</td>
                    <td className={`px-5 py-3 text-right num ${deltaClass}`}>{pct > 0 ? "+" : ""}{pct.toFixed(1)}%</td>
                  </tr>
                );
              })}
            </tbody>
            <tfoot>
              <tr className="border-t-2 border-border bg-muted/30">
                <td colSpan={3} className="px-5 py-3 font-bold uppercase tracking-wider text-xs">Total (Pre-VAT / Pre-Markup)</td>
                <td className="px-5 py-3 text-right num font-bold">{fmtMoney(t.original_subtotal, false)}</td>
                <td className="px-5 py-3 text-right num font-bold">{fmtMoney(t.revised_subtotal, false)}</td>
                <td className={`px-5 py-3 text-right num font-bold ${t.variance < 0 ? "text-emerald-600" : t.variance > 0 ? "text-red-600" : ""}`}>
                  {t.variance > 0 ? "+" : ""}{fmtMoney(t.variance, false)}
                </td>
                <td className={`px-5 py-3 text-right num font-bold ${t.variance < 0 ? "text-emerald-600" : t.variance > 0 ? "text-red-600" : ""}`}>
                  {t.variance_pct > 0 ? "+" : ""}{t.variance_pct.toFixed(2)}%
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>

      {/* Totals waterfall */}
      <div className="rounded-xl border border-border bg-card p-5" data-testid="totals-waterfall">
        <h3 className="font-heading text-lg font-bold mb-4">Grand Total Build-up</h3>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
          {[
            { l: "Original Subtotal", v: t.original_subtotal, muted: true },
            { l: "Revised Subtotal", v: t.revised_subtotal },
            { l: `− Discount ${fmtPct(data.params.global_discount_pct, 1)}`, v: t.subtotal_after_discount },
            { l: `+ Markup ${fmtPct(data.params.global_markup_pct, 1)}`, v: t.subtotal_after_markup },
            { l: `+ VAT ${data.params.vat_enabled ? fmtPct(data.params.vat_pct, 1) : "OFF"}`, v: t.vat_amount },
            { l: "Grand Total", v: t.grand_total, primary: true },
          ].map((x) => (
            <div key={x.l} className={`rounded-lg border p-3 ${x.primary ? "border-primary/50 bg-primary/10" : "border-border bg-background/40"}`}>
              <div className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground">{x.l}</div>
              <div className={`mt-1 num font-bold ${x.muted ? "text-muted-foreground" : x.primary ? "text-primary" : "text-foreground"}`}>{fmtMoney(x.v)}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
